import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Paper,
  Divider,
  Checkbox,
  FormControlLabel,
  FormGroup,
  InputAdornment,
  Button,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Card,
  CardContent,
  alpha,
} from '@mui/material';
import { LabeledField } from '@/__core/components/LabeledField';
import {
  FilterList,
  Search,
  Description,
  Code,
  PictureAsPdf,
  InsertDriveFile,
  Image,
  VideoFile,
  AudioFile,
  Archive,
  ExpandMore,
  DateRange,
  Person,
  Category,
  AttachFile,
} from '@mui/icons-material';


interface DocumentFiltersProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  selectedDocumentTypes: string[];
  onDocumentTypeChange: (typeId: string) => void;
  selectedUploadedBy: string[];
  onUploadedByChange: (uploaderId: string) => void;
  uniqueUploaders: string[];
  dateRange: { start: string; end: string };
  onDateRangeChange: (range: { start: string; end: string }) => void;
  documentTypes: any[];
  loading: boolean;
}

// Main DocumentFilters component
const DocumentFilters: React.FC<DocumentFiltersProps> = React.memo(({
  searchTerm,
  onSearchChange,
  selectedDocumentTypes,
  onDocumentTypeChange,
  selectedUploadedBy,
  onUploadedByChange,
  uniqueUploaders,
  dateRange,
  onDateRangeChange,
  documentTypes,
  loading,
}) => {
  // State to manage accordion expanded state - load from localStorage
  const [expandedAccordions, setExpandedAccordions] = useState(() => {
    try {
      const saved = localStorage.getItem('documentFiltersAccordionState');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.warn('Failed to load accordion state from localStorage');
    }
    return {
      documentTypes: true,
      uploadedBy: false,
      dateRange: false,
      fileTypes: false
    };
  });

  // Handler for accordion expansion
  const handleAccordionChange = useCallback((panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedAccordions((prev: typeof expandedAccordions) => {
      const newState = {
        ...prev,
        [panel]: isExpanded
      };
      // Save to localStorage
      try {
        localStorage.setItem('documentFiltersAccordionState', JSON.stringify(newState));
      } catch (e) {
        console.warn('Failed to save accordion state to localStorage');
      }
      return newState;
    });
  }, []);

  // Memoized handlers for checkbox changes
  const handleDocumentTypeChange = useCallback((typeId: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();
    onDocumentTypeChange(typeId);
  }, [onDocumentTypeChange]);

  const handleUploadedByChange = useCallback((uploaderId: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();
    onUploadedByChange(uploaderId);
  }, [onUploadedByChange]);

  // Shared accordion section component
  const FilterAccordion = React.memo(({ panel, icon, title, expanded, onToggle, children, noStack }: {
    panel: string;
    icon: React.ReactNode;
    title: string;
    expanded: boolean;
    onToggle: (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => void;
    children: React.ReactNode;
    noStack?: boolean;
  }) => (
    <Accordion
      expanded={expanded}
      onChange={onToggle(panel)}
      elevation={0}
      sx={{ mb: 1, '&:before': { display: 'none' }, '&.Mui-expanded': { margin: '0 0 8px 0' } }}
    >
      <AccordionSummary expandIcon={<ExpandMore />} sx={{ minHeight: 24, '&.Mui-expanded': { minHeight: 48 }, '& .MuiAccordionSummary-content': { margin: '4px 0' }, '& .MuiAccordionSummary-content.Mui-expanded': { margin: '4px 0' } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {icon}
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{title}</Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0, pt: 1 }}>
        {noStack ? children : <Stack spacing={1}>{children}</Stack>}
      </AccordionDetails>
    </Accordion>
  ));

  // Shared checkbox component for all filter items
  const FilterCheckbox = React.memo(({ label, isChecked, onChange }: {
    label: string;
    isChecked: boolean;
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  }) => (
    <FormControlLabel
      control={
        <Checkbox
          checked={isChecked}
          onChange={onChange}
          size="small"
          sx={{ color: 'primary.main', marginRight: 1, '&.Mui-checked': { color: 'primary.main' } }}
        />
      }
      label={
        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
          {label}
        </Typography>
      }
      sx={{ margin: 0, '& .MuiFormControlLabel-label': { fontSize: '0.875rem', color: 'text.primary' } }}
    />
  ));


  // Memoized document types list
  const documentTypesList = useMemo(() => {
    if (documentTypes.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
          Không có loại tài liệu nào
        </Typography>
      );
    }
    return documentTypes.map((type) => (
      <FilterCheckbox
        key={type.id}
        label={type.name}
        isChecked={selectedDocumentTypes.includes(type.id)}
        onChange={handleDocumentTypeChange(type.id)}
      />
    ));
  }, [documentTypes, selectedDocumentTypes, handleDocumentTypeChange]);

  // Memoized uploaders list
  const uploadersList = useMemo(() => {
    return uniqueUploaders.map((uploader) => (
      <FilterCheckbox
        key={uploader}
        label={uploader}
        isChecked={selectedUploadedBy.includes(uploader)}
        onChange={handleUploadedByChange(uploader)}
      />
    ));
  }, [uniqueUploaders, selectedUploadedBy, handleUploadedByChange]);

  return (
    <Paper 
      elevation={0}
      sx={{ 
        width: 320, 
        height: 'fit-content',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: 'background.paper'
      }}
    >

      <Box sx={{ p: 2 }}>
      

        <FilterAccordion panel="documentTypes" icon={<Category sx={{ fontSize: 18, color: 'primary.main' }} />} title="Loại tài liệu" expanded={expandedAccordions.documentTypes} onToggle={handleAccordionChange}>
          {documentTypesList}
        </FilterAccordion>

        <FilterAccordion panel="uploadedBy" icon={<Person sx={{ fontSize: 18, color: 'primary.main' }} />} title="Người tải lên" expanded={expandedAccordions.uploadedBy} onToggle={handleAccordionChange}>
          {uploadersList}
        </FilterAccordion>

        <FilterAccordion panel="dateRange" icon={<DateRange sx={{ fontSize: 18, color: 'primary.main' }} />} title="Ngày tải lên" expanded={expandedAccordions.dateRange} onToggle={handleAccordionChange} noStack>
          <Stack spacing={2}>
            <LabeledField label="Từ" labelWidth={60}>
              <TextField
                type="date"
                size="small"
                value={dateRange.start}
                onChange={(e) => onDateRangeChange({ ...dateRange, start: e.target.value })}
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
              />
            </LabeledField>
            <LabeledField label="Đến" labelWidth={60}>
              <TextField
                type="date"
                size="small"
                value={dateRange.end}
                onChange={(e) => onDateRangeChange({ ...dateRange, end: e.target.value })}
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
              />
            </LabeledField>
            {(dateRange.start || dateRange.end) && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => onDateRangeChange({ start: '', end: '' })}
                sx={{ borderRadius: 1.5, textTransform: 'none', fontSize: '0.75rem', py: 0.5, mt: 1 }}
              >
                Xóa bộ lọc ngày
              </Button>
            )}
          </Stack>
        </FilterAccordion>

        <FilterAccordion panel="fileTypes" icon={<AttachFile sx={{ fontSize: 18, color: 'primary.main' }} />} title="Loại file" expanded={expandedAccordions.fileTypes} onToggle={handleAccordionChange} noStack>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {[
              { type: 'image', icon: <Image />, label: 'Hình ảnh', color: '#4caf50' },
              { type: 'pdf', icon: <PictureAsPdf />, label: 'PDF', color: '#f44336' },
              { type: 'word', icon: <Description />, label: 'Word', color: '#2196f3' },
              { type: 'video', icon: <VideoFile />, label: 'Video', color: '#ff9800' },
              { type: 'audio', icon: <AudioFile />, label: 'Audio', color: '#9c27b0' },
              { type: 'archive', icon: <Archive />, label: 'Nén', color: '#607d8b' },
            ].map((fileType) => (
              <Chip
                key={fileType.type}
                icon={fileType.icon}
                label={fileType.label}
                variant="outlined"
                size="small"
                onClick={() => {}}
                sx={{
                  borderRadius: 2, fontSize: '0.75rem', height: 28,
                  borderColor: fileType.color, color: fileType.color,
                  '&:hover': { bgcolor: alpha(fileType.color, 0.1), borderColor: fileType.color },
                  '& .MuiChip-icon': { color: fileType.color, fontSize: 16 }
                }}
              />
            ))}
          </Box>
        </FilterAccordion>
      </Box>
    </Paper>
  );
}, (prevProps, nextProps) => {
  // Only re-render if props actually changed
  const propsChanged = Object.keys(prevProps).some(key => {
    if (key === 'uniqueUploaders') {
      return JSON.stringify((prevProps as any)[key]) !== JSON.stringify((nextProps as any)[key]);
    }
    return (prevProps as any)[key] !== (nextProps as any)[key];
  });
  
  return !propsChanged;
});

DocumentFilters.displayName = 'DocumentFilters';

export default DocumentFilters;
